<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import type { LocationItem, ResultsLocation, ResultsLocationCountry, ResultsLocationProximity } from '~/core/types'
  import { LocationType } from '~/core/types'

  import Item from './Item.vue'

  const props = withDefaults(defineProps<Props>(), {
    activeSearch: false,
    loading: false,
    noResult: false,
  })

  const emit = defineEmits<{
    (e: 'input', v: ResultsLocation): void
    (e: 'currentLocationError' | 'click-outside' | 'inputClick' | 'blur'): void
  }>()

  const { geoLocation, locationEnabled } = useLocation()

  type Props = {
    activeSearch?: boolean
    loading?: boolean
    noResult?: boolean
    groups: {
      label: string
      options: LocationItem[]
      type: LocationType
    }[]
    lastResults?: LocationItem[]
  }

  const { t } = useI18n()

  function handleGetPosition() {
    try {
      if (!geoLocation.value) {
        emit('currentLocationError')
        throw new Error('Error while retrieving location')
      }
      const proximityItem: ResultsLocationProximity = {
        type: LocationType.PROXIMITY,
        value: geoLocation.value,
      }
      emit('input', proximityItem)
    } catch (e) {
      const { message } = e as GeolocationPositionError
      alert(message)
    }
  }

  function handleFranceInput() {
    const franceItem: ResultsLocationCountry = {
      type: LocationType.COUNTRY,
      value: {
        label: 'Toute la France',
        code: 'FR',
      },
    }
    emit('input', franceItem)
  }

  function input(value: LocationItem) {
    emit('input', value)
  }
</script>

<template>
  <div
    class="c-location-input"
    :class="{ 'is-loading': loading }"
  >
    <template v-if="activeSearch && !noResult">
      <div
        v-for="(group, k1) of groups"
        :key="k1"
        class="group"
      >
        <template v-if="group.options.length">
          <p class="group-label">
            {{ group.label }}
          </p>
          <div class="options">
            <Item
              v-for="(option, k2) in group.options"
              :key="k2"
              :label="option.fancyLabel"
              @click.stop="input(option)"
            />
          </div>
        </template>
      </div>
    </template>
    <template v-else-if="activeSearch && noResult && loading">
      <div class="empty section">
        <Item
          icon="arrow"
          :label="t('products.loading-results')"
        />
      </div>
    </template>
    <template v-else-if="activeSearch && noResult && !loading">
      <div class="empty section">
        <Item
          icon="no-result"
          :label="t('products.no-results')"
        />
      </div>
    </template>
    <template v-else>
      <div
        class="proximity section"
        :class="{disabled: !locationEnabled}"
      >
        <Item
          icon="sight-alt"
          :label="locationEnabled ? `À proximité`: `À proximité<br><small>Veuillez activer la geolocalisation</small>`"
          :disabled="!locationEnabled"
          @click.stop="handleGetPosition"
        />
      </div>
      <div class="france section">
        <Item
          icon="france"
          label="Toute la France"
          @click.stop="handleFranceInput"
        />
      </div>
      <Divider v-if="props.lastResults?.length" />
      <div
        v-if="props.lastResults?.length"
        class="section latest-locations"
      >
        <span>Lieux récents</span>
        <div class="location-items">
          <Item
            v-for="(lastResult, k3) of lastResults"
            :key="k3"
            :label="lastResult.fancyLabel"
            @click.stop="input(lastResult)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.c-location-input {
  flex: 1 0 auto;
  padding: 1em 0;
  opacity: 1;
  transition: opacity 0.15s ease-out;

  .group {
    .group-label {
      padding: 1em 1.5em;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1.125em;
      font-weight: bold;
      opacity: 0.8;
      text-transform: uppercase;
    }
  }

  .c-divider {
    width: calc(100% - 3em);
    margin: 1em 1.5em;
  }

  .section {
    display: flex;
    box-sizing: border-box;
    flex-direction: row;
    align-items: center;
    transition: background-color ease-in-out 0.15s;

    &.latest-locations {
      flex-direction: column;
      align-items: flex-start;

      span {
        padding: 1rem 1.5rem;
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1.125em;
        font-weight: bold;
        opacity: 0.8;
        text-transform: uppercase;
      }
    }

    &.proximity{
      flex-direction: column;

      &.disabled {
        opacity: .5;
      }
    }
  }

  &.is-loading {
    opacity: 0.25;
  }
}
</style>
